import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import AuthGate from "./AuthGate";
import { session as authSession } from "../auth";
import { COPYRIGHT_NOTICE, CopyrightNotice } from "./components/CopyrightNotice";
import PublicSite from "./pages/PublicSite";
import "./styles.css";

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element");

// Migration compatibility: some frozen V1 copy and historical enum values still
// contain the former CarpIntel brand internally. Keep those identifiers/values
// stable for migration safety, but never expose the old brand to anglers.
const DISPLAY_TEXT_REPLACEMENTS = [
  ["CarpIntel Advisor", "CatchCompass Advisor"],
  ["CarpIntel · ", "CatchCompass · "],
  ["CarpIntel statistics", "CatchCompass statistics"],
  ["CarpIntel evidence", "CatchCompass evidence"],
  ["CarpIntel can compare", "CatchCompass can compare"],
  ["CarpIntel user", "CatchCompass user"],
  ["© 2026 CatchCompass. All rights reserved.", COPYRIGHT_NOTICE],
] as const;

function replaceDisplayText(value: string): string {
  return DISPLAY_TEXT_REPLACEMENTS.reduce(
    (next, [from, to]) => next.replaceAll(from, to),
    value,
  );
}

function scrubDisplayText(root: Node) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    const parent = textNode.parentElement;
    if (!parent || ["SCRIPT", "STYLE", "TEXTAREA"].includes(parent.tagName))
      continue;
    const next = replaceDisplayText(textNode.data);
    if (next !== textNode.data) textNode.data = next;
  }
}

const displayTextObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === "characterData") {
      scrubDisplayText(mutation.target.parentNode ?? mutation.target);
      continue;
    }
    for (const node of mutation.addedNodes) scrubDisplayText(node);
  }
});
displayTextObserver.observe(rootElement, {
  childList: true,
  subtree: true,
  characterData: true,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const publicPaths = new Set(["/", "/privacy", "/terms"]);
const currentPath = typeof window === "undefined" ? "/" : window.location.pathname;
const isPublicPage = publicPaths.has(currentPath);
const isLoginPage = currentPath === "/login";

function isStandaloneCatchCompass() {
  if (typeof window === "undefined") return false;
  const iosNavigator = window.navigator as Navigator & { standalone?: boolean };
  return (
    iosNavigator.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true
  );
}

const shouldResolveInstalledRoot =
  currentPath === "/" && isStandaloneCatchCompass();

function PublicEntry() {
  const [checkingInstalledSession, setCheckingInstalledSession] = React.useState(
    shouldResolveInstalledRoot,
  );

  React.useEffect(() => {
    if (!shouldResolveInstalledRoot) return;

    let active = true;
    void authSession
      .status()
      .then((state) => {
        if (!active) return;

        // Older iPhone Home Screen installs can keep reopening the URL they were
        // originally saved from (usually /) even though the manifest now starts
        // at /app. If the installed app still has any authenticated CatchCompass
        // state, route it through AuthGate instead of showing the marketing site.
        if (state.status !== "signed_out") {
          window.location.replace("/app");
          return;
        }

        setCheckingInstalledSession(false);
      })
      .catch(() => {
        if (active) setCheckingInstalledSession(false);
      });

    return () => {
      active = false;
    };
  }, []);

  if (checkingInstalledSession) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4 text-center text-sm text-secondary">
        Opening CatchCompass…
      </div>
    );
  }

  return <PublicSite />;
}

createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {isPublicPage ? (
        <PublicEntry />
      ) : (
        <div className="relative min-h-dvh bg-background text-primary">
          <AuthGate>
            <App />
          </AuthGate>
          <footer
            className={
              isLoginPage
                ? "px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 text-center"
                : "px-4 pb-32 pt-8 text-center"
            }
            aria-label="Copyright notice"
          >
            <CopyrightNotice />
          </footer>
        </div>
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);

queueMicrotask(() => scrubDisplayText(rootElement));

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // Offline support is progressive enhancement; never block the app shell.
    });
  });
}
